import {
  AgeRangeEnum,
  BodyPartEnum,
  ModalityEnum,
  SexEnum,
} from '@services/api'
import { isValidPhoneNumber } from 'react-phone-number-input'

import * as Yup from 'yup'
import { pathwayhealth } from './constants'
import {
  ActivityLevel,
  HotLabEquipmentType,
  MonitoringMethod,
  HospitalUnitType,
  MssRequestPriority,
  MssRequestStatus,
  PaymentModeType,
  ProductType,
  Side,
  StorageType,
  WasteStatus,
  WasteType,
  CompoundType,
  VisualQualityControlType,
  CustomQualityControlType,
  CompoundProductionStepType,
  SupplierType,
  ProductPackageUom,
  ProductOrderCancellationReason,
  StockReason,
  ProductBatchDto,
  ProductCatalogDto,
  SupplierContactType,
  ProductOrderDto,
  ProductOrderStatus,
  SupplierPaymentMethod,
  PosologySliceType,
  PosologyPatientType,
  CreatePosologySliceDto,
  EANMClass,
  IsotopeEmissionType,
  IsotopeUsage,
  QCType,
} from '@services/dtos'
import { FileTypeEnum } from '../common/interfaces'
import moment from 'moment'
import { CareTypeEnum } from '@services/functionalUnitService'
import { ProductOrderFormStep } from '@components/forms/productOrder/ProductOrderForm'
import { ProductOrderReceiptFormStep } from '@components/forms/productOrderReceipt/ProductOrderReceiptForm'
import { getCurrentPrice } from './product.utils'
import { isNil } from 'lodash'
import { validatePosologySlices } from './validatePosologySlices'

// const RegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

export const signFailSchema = Yup.object().shape({
  failure: Yup.number().required('validation.field.required'),
  period: Yup.number().required('validation.field.required'),
  window: Yup.number().required('validation.field.required'),
  timeout: Yup.number().required('validation.field.required'),
})

const optionalDecimalField = () =>
  Yup.number()
    .typeError('Format non valide')
    .transform((value, originalValue) =>
      originalValue === '' || isNaN(value) ? null : value,
    )
    .test('max-2-decimals', 'Format non valide', (value) => {
      if (value === null || value === undefined) return true
      return Number.isInteger(value * 100)
    })
    .nullable()
    .optional()

export const loginSchema = Yup.object().shape({
  email: Yup.string()
    .lowercase()
    .trim()
    .email('validation.email.invalid')
    .required('validation.email.required'),
  password: Yup.string().required('validation.password.required'),
  // captcha: Yup.string().required('validation.password.required'),
})

export const forgottenPassowrdSchema = Yup.object().shape({
  email: Yup.string()
    .lowercase()
    .trim()
    .email('validation.email.invalid')
    .required('validation.email.required'),
})

export const registerSchema = Yup.object().shape({
  email: Yup.string()
    .lowercase()
    .trim()
    .email('validation.email.invalid')
    .required('validation.email.required'),
  newPassword: Yup.string()
    .required('validation.password.required')
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
      'validation.password.strength',
    ),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'validation.password.match')
    .required('validation.password.required'),
})
// export const registerSchema = Yup.object().shape({
//   email: Yup.string().required('email.required').email('email.invalid'),
//   phoneNumber: Yup.string()
//     .required('register.msg.error.phone.required')
//     .test('isPhone', 'register.msg.error.phone.format', (v) =>
//       isValidPhoneNumber(v)
//     ),
// })

export const forgotpasswordSchema = Yup.object().shape({
  email: Yup.string().email('email.invalid').required('email.required'),
})

export const resetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('validation.password.required')
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
      'validation.password.strength',
    ),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'validation.password.match')
    .required('validation.password.required'),
})

export const verifyEmailSchema = Yup.object().shape({
  password: Yup.string()
    .required('validation.password.required')
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
      'validation.password.strength',
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'validation.password.match')
    .required('validation.password.required'),
})

export const updateEmailSchema = Yup.object().shape({
  email: Yup.string().email('email.invalid').required('email.required'),
})

export const createDoctorSchema = Yup.object().shape({
  email: Yup.string().email('email.invalid').required('email.required'),
  phoneNumber: Yup.string()
    .test('isPhone', 'register.msg.error.phone.format', (v) =>
      isValidPhoneNumber(v || ''),
    )
    .required('register.msg.error.phone.required'),
})

export const phoneCodeSchema = Yup.object().shape({
  code: Yup.string()
    .required('validation.field.required')
    .length(6)
    .matches(/^[0-9]{6}$/, 'validation.code.match'),
})

export const addClientSchema = Yup.object().shape({
  name: Yup.string().required('validation.field.required'),
  status: Yup.string().required('validation.field.required'),
  siretNumber: Yup.string()
    .required('validation.field.required')
    .length(9)
    .matches(/^[0-9]{9}$/, 'validation.code.codeEin'),
  // address: Yup.string().required('validation.field.required'),
  streetAddress: Yup.string().required('validation.field.required'),
  postalCode: Yup.string().required('validation.field.required'),
  city: Yup.string().required('validation.field.required'),
  country: Yup.string().required('validation.field.required'),
  phoneNumber: Yup.string()
    .required('validation.field.required')
    .min(14, 'phone number is not valid'),
  fax: Yup.string(),
  website: Yup.string(),
  email: Yup.string()
    .email('validation.email.invalid')
    .required('validation.email.required'),
})

export const addStaffSchema = Yup.object().shape({
  firstName: Yup.string().required('validation.field.required'),
  lastName: Yup.string().required('validation.field.required'),
  phoneNumber: Yup.string()
    .required('validation.field.required')
    .min(14, 'phone number is not valid'),
  email: Yup.string()
    .email('validation.email.invalid')
    .required('validation.email.required'),
})

export const addMemberSchema = Yup.object().shape({
  firstName: Yup.string().required('validation.field.required'),
  lastName: Yup.string().required('validation.field.required'),
  siteIds: Yup.array()
    .of(Yup.number())
    .min(1, 'validation.field.required')
    .required('validation.field.required'),
  password: Yup.string().when('$isEdit', {
    is: (isEdit: boolean) => !isEdit,
    then: (schema) => schema.required('Password is required'),
    otherwise: (schema) => schema.optional(),
  }),
  email: Yup.string()
    .email('validation.email.invalid')
    .required('validation.email.required'),
})

export const addRoleSchema = Yup.object().shape({
  name: Yup.string().required('validation.field.required'),
  description: Yup.string().required('validation.field.required'),
  siteId: Yup.string().required('validation.field.required'),
})

export const addQuestionSchema = Yup.object().shape({
  question: Yup.string().required('validation.field.required'),
  siteId: Yup.string().required('validation.field.required'),
  type: Yup.string().required('validation.field.required'),
})

export const siteSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  capacity: Yup.number().nullable().optional(),
  // siteTypeId: Yup.string().required('validation.field.required'),
  finessNumber: Yup.string().required('validation.field.required'),
  email: Yup.string().email('validation.email.invalid').nullable().optional(),
  website: Yup.string().nullable().optional(),
  addresses: Yup.array()
    .of(
      Yup.object().shape({
        streetAddress: Yup.string().required('validation.field.required'),
        postalCode: Yup.number().required('validation.field.required'),
        city: Yup.string().required('validation.field.required'),
        country: Yup.string().required('validation.field.required'),
        phoneNumber: Yup.string().required('validation.field.required'),
        fax: Yup.string().nullable().optional(),
      }),
    )
    .min(1, 'Au moins une adresse est requise')
    .required('validation.field.required'),
})

export const addressSchema = Yup.object().shape({
  line1: Yup.string().required('validation.field.required'),
  line2: Yup.string().optional(),
  postalCode: Yup.string().required('validation.field.required'),
  city: Yup.string().required('validation.field.required'),
  country: Yup.string().required('validation.field.required'),
  locationName: Yup.string().optional(),
  locationType: Yup.string()
    .oneOf(['Clinique', 'Hospital', 'Etablissement'])
    .optional()
    .nullable(),
  finessNumber: Yup.string().optional(),
})

export const mssEmailsSchema = Yup.object().shape({
  email: Yup.string().email('Invalide').required('validation.field.required'),
  active: Yup.boolean(),
  favorite: Yup.boolean(),
})

export const doctorSchema = Yup.object().shape(
  {
    firstName: Yup.string().required('validation.field.required'),
    lastName: Yup.string().required('validation.field.required'),
    rppsNumber: Yup.string().optional(),
    skill: Yup.string(),
    profession: Yup.string().optional(),
    category: Yup.string().optional(),
    email: Yup.string().email().optional(),
    addresses: Yup.array(addressSchema),
    mssEmails: Yup.array(mssEmailsSchema),
    phoneNumber: Yup.string().nullable().optional(),
    mobileNumber: Yup.string().nullable().optional(),
  },
  [['email', 'emailPro']],
)

export const itEquipmentSchema = Yup.object().shape({
  siteId: Yup.string().required('validation.field.required'),
  ip: Yup.string().required('validation.field.required'),
  port: Yup.string().required('validation.field.required'),
})

export const medicalEquipmentSchema = Yup.object().shape({
  manufacturer: Yup.string().optional(),
  model: Yup.string().required('validation.field.required'),
  commissioningDate: Yup.date().required('validation.field.required'),
  examRoomId: Yup.string().required('validation.field.required'),
})

const posologySliceSchema = Yup.object().shape({
  weightMin: Yup.number()
    .nullable()
    .optional()
    .test(
      'weightMin-less-than-max',
      'validation.weightMin.greaterThanMax',
      function (value) {
        const { weightMax } = this.parent
        if (isNil(value) || isNil(weightMax)) {
          return true
        }
        return value < weightMax
      },
    ),
  weightMax: Yup.number()
    .nullable()
    .optional()
    .test(
      'weightMax-greater-than-min',
      'validation.weightMax.lessThanMin',
      function (value) {
        const { weightMin } = this.parent
        if (isNil(value) || isNil(weightMin)) {
          return true
        }
        return value > weightMin
      },
    ),
  type: Yup.mixed<PosologySliceType>()
    .oneOf(Object.values(PosologySliceType))
    .required('validation.field.required'),
  patientType: Yup.mixed<PosologyPatientType>()
    .oneOf(Object.values(PosologyPatientType))
    .required('validation.field.required'),
  eanmClass: Yup.mixed<EANMClass>().when('type', {
    is: PosologySliceType.EANM,
    then: (schema) =>
      schema
        .oneOf(Object.values(EANMClass))
        .required('validation.field.required'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  eanmBaseActivityMBq: Yup.number().when('type', {
    is: PosologySliceType.EANM,
    then: (schema) =>
      schema
        .moreThan(0, 'validation.eanmBaseActivityMBq.moreThanZero')
        .required('validation.eanmBaseActivityMBq.required'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  eanmMinActivityMBq: Yup.number().when('type', {
    is: PosologySliceType.EANM,
    then: (schema) =>
      schema
        .moreThan(0, 'validation.eanmMinActivityMBq.moreThanZero')
        .required('validation.eanmMinActivityMBq.required'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  activityMBq: Yup.number().when('type', {
    is: (type: PosologySliceType) => type !== PosologySliceType.EANM,
    then: (schema) =>
      schema
        .moreThan(0, 'validation.activityMBq.moreThanZero')
        .required('validation.activityMBq.required'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
})

const examCompoundSchema = Yup.object().shape({
  compoundId: Yup.string().required('validation.field.required'),
  posologySlices: Yup.array()
    .of(posologySliceSchema)
    .min(1, 'validation.field.required')
    .required('validation.field.required')
    .test('no-overlap', 'validation.posology.overlap', (slices) => {
      if (!slices?.length) {
        return true
      }
      const invalidIndices = validatePosologySlices(
        slices as CreatePosologySliceDto[],
      )
      return invalidIndices.size === 0
    }),
})

export const procedureSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  modality: Yup.string().optional(),
  procedureTime: Yup.string()
    .required('validation.field.required')
    .nullable()
    .optional(),
  isNuclear: Yup.boolean().optional(),
  sex: Yup.mixed<SexEnum>().oneOf(Object.values(SexEnum)).nullable().optional(),
  bodyPart: Yup.mixed<BodyPartEnum>().nullable().optional(),
  ageRange: Yup.mixed<AgeRangeEnum>().optional().nullable(),
  siteId: Yup.string().required('validation.field.required'),
  examCompounds: Yup.array().of(examCompoundSchema).optional(),
  // instruction: Yup.mixed<InstructionEnum>().oneOf(
  //   Object.values(InstructionEnum),
  // ),
})

export const examTypeSchema = Yup.object().shape({
  siteId: Yup.string().required('validation.field.required'),
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
})

export const updateDoctorSchema = Yup.object().shape(
  {
    firstName: Yup.string().required('validation.field.required'),
    lastName: Yup.string().required('validation.field.required'),
    rppsNumber: Yup.string().required('validation.field.required'),
    city: Yup.string().required('validation.field.required'),
    skill: Yup.string(),
    profession: Yup.string(),
    category: Yup.string().required('validation.field.required'),
    country: Yup.string().required('validation.field.required'),
    website: Yup.string(),
    fax: Yup.string(),
    phoneNumber: Yup.string()
      .required('validation.field.required')
      .min(14, 'phone number is not valid'),

    // phoneNumber: Yup.string().required('validation.field.required')
    // .length(9)
    // .matches(/^[0-9]{9}$/, 'validation.code.codeEin'),

    email: Yup.string()
      .email('validation.email.invalid')
      //.required('validation.field.required'),
      .when('emailPro', {
        is: (emailPro: string) => !emailPro || emailPro.length === 0,
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
    emailPro: Yup.string()
      .email('validation.email.invalid')
      .when('email', {
        is: (email: string) => !email || email.length === 0,
        then: Yup.string().required('validation.field.required'),
        otherwise: Yup.string(),
      }),
  },
  [['email', 'emailPro']],
)

export const updateGeneralInfoSchema = Yup.object().shape({
  firstName: Yup.string().required('validation.field.required'),
  lastName: Yup.string().required('validation.field.required'),
  sex: Yup.string().required('validation.field.required'),
  street: Yup.string().required('validation.field.required'),
  postalCode: Yup.string().required('validation.field.required'),
  city: Yup.string().required('validation.field.required'),
  country: Yup.string().required('validation.field.required'),
  birthPlace: Yup.string().required('validation.field.required'),
  birthDate: Yup.date().required('validation.field.required'),
  ssn: Yup.string().required('validation.field.required'),
})

export const addRoomSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  openingTime: Yup.date().required('validation.field.required'),
  breakTime: Yup.date().required('validation.field.required'),
  reopeningTime: Yup.date().required('validation.field.required'),
  closingTime: Yup.date().required('validation.field.required'),
  color: Yup.string().required('validation.field.required'),
})
export const addReceptionSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  siteId: Yup.string().required('validation.field.required'),
})
export const addWaitingRoomSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  receptionId: Yup.string().required('validation.field.required'),
})
export const addOfficeSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  siteId: Yup.string().required('validation.field.required'),
})
export const addLabWorkstationSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  siteId: Yup.string().required('validation.field.required'),
})
export const addPrepRoomSchema = Yup.object().shape({
  code: Yup.string().required('validation.field.required'),
  label: Yup.string().required('validation.field.required'),
  examRoomId: Yup.string().required('validation.field.required'),
})

export const pidSchema = Yup.object().shape({
  PID1: Yup.string().required('validation.field.required'),
  PID2: Yup.string().required('validation.field.required'),
  firstName: Yup.string().required('validation.field.required'),
  lastName: Yup.string().required('validation.field.required'),
  motherMaidenName: Yup.string().required('validation.field.required'),
  birthDate: Yup.date().required('validation.field.required'),
  sex: Yup.string().required('validation.field.required'),
  streetAddress: Yup.string().required('validation.field.required'),
  city: Yup.string().required('validation.field.required'),
  postalCode: Yup.string().required('validation.field.required'),
  country: Yup.string().required('validation.field.required'),
  countryCode: Yup.string().required('validation.field.required'),
  // phoneNumber: Yup.string()
  //   .test('isPhone', 'register.msg.error.phone.format', (v) =>
  //     isValidPhoneNumber(v),
  //   )
  //   .required('register.msg.error.phone.required'),
  email: Yup.string()
    .lowercase()
    .trim()
    .email('validation.email.invalid')
    .required('validation.email.required'),
  accountNumber: Yup.string().required('validation.field.required'),
  systemName: Yup.string().required('validation.field.required'),
  ssn: Yup.string().required('validation.field.required'),
})

export const pv1Schema = Yup.object().shape({
  attendingdoctorId: Yup.string().required('validation.field.required'),
  attendingDoctorFirstName: Yup.string().required('validation.field.required'),
  attendingDoctorLastName: Yup.string().required('validation.field.required'),
  attendingDoctorPrefix: Yup.string().required('validation.field.required'),
  attendingdoctorIdType: Yup.string().required('validation.field.required'),
  referringdoctorId: Yup.string().required('validation.field.required'),
  referringDoctorFirstName: Yup.string().required('validation.field.required'),
  referringDoctorLastName: Yup.string().required('validation.field.required'),
  referringDoctorPrefix: Yup.string().required('validation.field.required'),
  referringdoctorIdType: Yup.string().required('validation.field.required'),
})

export const orcSchema = Yup.object().shape({
  placeOrderNumber: Yup.string().required('validation.field.required'),
  fillerOrderNumber: Yup.string().required('validation.field.required'),
  orderStatus: Yup.string().required('validation.field.required'),
  quantity: Yup.string().required('validation.field.required'),
  interval: Yup.string().required('validation.field.required'),
  priority: Yup.string().required('validation.field.required'),
  startDateTime: Yup.date().required('validation.field.required'),
  attendingdoctorId: Yup.string().required('validation.field.required'),
  attendingDoctorFirstName: Yup.string().required('validation.field.required'),
  attendingDoctorLastName: Yup.string().required('validation.field.required'),
  attendingDoctorPrefix: Yup.string().required('validation.field.required'),
  attendingdoctorIdType: Yup.string().required('validation.field.required'),
})

export const obrSchema = Yup.object().shape({
  OBRID: Yup.string().required('validation.field.required'),
  entityIdentifier: Yup.string().required('validation.field.required'),
  systemName: Yup.string().required('validation.field.required'),
  accessionNumber: Yup.string().required('validation.field.required'),
  studyCode: Yup.string().required('validation.field.required'),
  studyTitle: Yup.string().required('validation.field.required'),
  plannedDate: Yup.date().required('validation.field.required'),
  attendingdoctorId: Yup.string().required('validation.field.required'),
  attendingDoctorFirstName: Yup.string().required('validation.field.required'),
  attendingDoctorLastName: Yup.string().required('validation.field.required'),
  attendingDoctorPrefix: Yup.string().required('validation.field.required'),
  attendingdoctorIdType: Yup.string().required('validation.field.required'),
  modality: Yup.string().required('validation.field.required'),
  quantity: Yup.string().required('validation.field.required'),
  interval: Yup.string().required('validation.field.required'),
  priority: Yup.string().required('validation.field.required'),
  pointOfCare: Yup.string().required('validation.field.required'),
  room: Yup.string().required('validation.field.required'),
})

export const obxSchema = Yup.object().shape({
  OBXID: Yup.string().required('validation.field.required'),
  systemName: Yup.string().required('validation.field.required'),
  DOC: Yup.string().required('validation.field.required'),
  JPEG: Yup.string().required('validation.field.required'),
  Base64: Yup.string().required('validation.field.required'),
  PDFBase64: Yup.string().required('validation.field.required'),
})

export const zdsSchema = Yup.object().shape({
  studyInstanceUID: Yup.string().required('validation.field.required'),
  ApplicationID: Yup.string().required('validation.field.required'),
  Application: Yup.string().required('validation.field.required'),
  DICOM: Yup.string().required('validation.field.required'),
})

export const ambulanceCompanySchema = Yup.object()
  .shape({
    name: Yup.string().required('validation.field.required'),
    address: Yup.string(),
    zipCode: Yup.number(),
    city: Yup.string(),
    phone1: Yup.string().required('validation.field.required'),
    phone2: Yup.string(),
    siret: Yup.string().optional().max(14),
    email: Yup.string().email('validation.email.invalid'),
  })
  .shape({})

export const patientTransportSchema = Yup.object()
  .shape({
    name: Yup.string().required('validation.field.required'),
    phoneNumber: Yup.string().required('validation.field.required'),
    companyId: Yup.number().optional(),
    plateNumber: Yup.string().optional(),
    transportType: Yup.string().required('validation.field.required'),
  })
  .shape({})
export const patientGeneralnfoSchema = Yup.object().shape({
  firstName: Yup.string().required('validation.field.required'),
  lastName: Yup.string().required('validation.field.required'),
  gender: Yup.string().required('validation.field.required'),
  email: Yup.string().required('validation.field.required').email(),
  phoneNumber: Yup.string().required('validation.field.required'),
})
// TODO: approv new schemas and enum
export const BankSchema = Yup.object().shape({
  bankCode: Yup.string().required('Required').max(50, 'Maximum 50 characters'),
  bankLabel: Yup.string().required('Required').max(50, 'Maximum 50 characters'),
})
export const CashRegisterSchema = Yup.object().shape({
  cashRegisterCode: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  cashRegisterLabel: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  siteCode: Yup.string().required('Required').max(50, 'Maximum 50 characters'),
  logCode: Yup.string().max(50, 'Maximum 50 characters'),
  responsible: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  checkboxOpen: Yup.boolean(),
  checkboxClose: Yup.boolean(),
  startDate: Yup.string()
    .required('Required')
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
  endDate: Yup.string()
    .optional()
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
})
export enum MovementTypeEnum {
  'IN' = 'IN',
  'OUT' = 'OUT',
  'BOTH' = 'BOTH',
}
export const CashRegisterMovSchema = Yup.object().shape({
  cashRegisterCode: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  cashRegisterLabel: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  generationAccoutantWrite: Yup.boolean(),
  movementType: Yup.mixed<MovementTypeEnum>().oneOf(
    Object.values(MovementTypeEnum),
  ),
})
export enum PaymentModeTypeEnum {
  'CB' = 'CB',
  'CHQ' = 'CHQ',
  'E' = 'E',
  'G' = 'G',
  'PP' = 'PP',
}
export const PaymentModeSchema = Yup.object().shape({
  paymentModeCode: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  paymentModeLabel: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  paymentModeType: Yup.mixed<PaymentModeTypeEnum>().oneOf(
    Object.values(PaymentModeTypeEnum),
  ),
  accountSectionCode: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  checkboxBank: Yup.boolean(),
  checkboxCheck: Yup.boolean(),
  checkboxPatientBank: Yup.boolean(),
})
export enum FeesJournalCodeEnum {
  'BQCARD' = 'Banque honoraires cardio',
  'CA282_H' = 'Banque honoraires CA',
  'SG998_H' = 'Banque honoraires SG',
}
export enum relaunchTriggerWhoEnum {
  'BYNAME' = 'Par les Nom/Prénom de l\'assuré',
  'BYNUM' = 'Par le numéro de facture',
}
export enum relaunchTriggerWhenEnum {
  'BYCOME' = 'Date d\'accueil',
  'BYFICHE' = 'Date du bordereau',
}
export const FinanceCaisseSchema = Yup.object().shape({
  auxiliaryAccountNumber: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  technicalFlatRateJournalCode: Yup.mixed<FeesJournalCodeEnum>().oneOf(
    Object.values(FeesJournalCodeEnum),
  ),
  feesJournalCode: Yup.mixed<FeesJournalCodeEnum>().oneOf(
    Object.values(FeesJournalCodeEnum),
  ),
  ignoreInRelaunches: Yup.boolean(),
  ignoreInGeneration: Yup.boolean(),
  manageClaimForms: Yup.boolean(),
  manageDisputes: Yup.boolean(),
  oneInvoicePerPage: Yup.boolean(),
  relaunchTriggerBy: Yup.mixed<relaunchTriggerWhoEnum>().oneOf(
    Object.values(relaunchTriggerWhoEnum),
  ),
  relaunchTriggerDate: Yup.mixed<relaunchTriggerWhenEnum>().oneOf(
    Object.values(relaunchTriggerWhenEnum),
  ),
  payerOrganization: Yup.string().required('Required'),
  comment: Yup.string().required('Required').max(300, 'Maximum 300 characters'),
})
export enum contactStatusEnum {
  'DR' = 'Doctor',
  'DRE' = 'Doctore',
  'MR' = 'Monsieur',
  'MME' = 'Madame',
  'ENF' = 'Enfant',
}
export const fundGeneralInfoSchema = Yup.object().shape({
  organizationCode: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  organizationName: Yup.string()
    .required('Required')
    .max(100, 'Maximum 100 characters'),
  refundRate: Yup.number()
    .required('Required')
    .min(0, 'Must be a valid number')
    .max(100, 'Must be a valid number'),
  lastBordereau: Yup.string().required('Required'),
  address: Yup.string().required('Required').max(200, 'Maximum 200 characters'),
  postalCode: Yup.number()
    .required('Required')
    .min(10000, 'Must be a valid postal code')
    .max(99999, 'Must be a valid postal code'),
  city: Yup.string().required('Required').max(100, 'Maximum 100 characters'),
  contactName: Yup.string()
    .required('Required')
    .max(100, 'Maximum 100 characters'),
  contactStatus: Yup.mixed<contactStatusEnum>().oneOf(
    Object.values(contactStatusEnum),
  ),
  email: Yup.string().email('Invalid email').required('Required'),
  professionalPhone: Yup.string()
    .required('Required')
    .matches(/^\d{10}$/, 'Must be a valid phone number'),
  fax: Yup.string()
    .optional()
    .matches(/^\d{10}$/, 'Must be a valid fax number'),
  socialSecurityRegime: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  socialSecurityFund: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  socialSecurityCenter: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  toTransmit: Yup.boolean().required('Required'),
  monacoFund: Yup.boolean().required('Required'),
  activeFrom: Yup.string()
    .required('Required')
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
  activeTo: Yup.string()
    .optional()
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
})
export enum TypeMutuelleEnum {
  'MUTUELLE' = 'Mutuelle',
  'AMC' = 'AMC',
}
export enum ThirdPartyPaymentEnum {
  'TP' = 'Tiers-payant (mutuel)',
  'HE' = '100% Honoraires (org.extérieur)',
  'HEFT' = '100% Honoraires + FT (org.extérieur)',
}
export const mutuelleGeneralInfoSchema = Yup.object().shape({
  organizationCode: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  organizationName: Yup.string()
    .required('Required')
    .max(100, 'Maximum 100 characters'),
  type: Yup.mixed<TypeMutuelleEnum>().oneOf(Object.values(TypeMutuelleEnum)),
  refundRate: Yup.number()
    .required('Required')
    .min(0, 'Must be a valid number')
    .max(100, 'Must be a valid number'),
  lastBordereau: Yup.string().required('Required'),
  thirdPartyPayment: Yup.mixed<ThirdPartyPaymentEnum>().oneOf(
    Object.values(ThirdPartyPaymentEnum),
  ),
  address: Yup.string().required('Required').max(200, 'Maximum 200 characters'),
  postalCode: Yup.number()
    .required('Required')
    .min(10000, 'Must be a valid postal code')
    .max(99999, 'Must be a valid postal code'),
  city: Yup.string().required('Required').max(100, 'Maximum 100 characters'),
  contactName: Yup.string()
    .required('Required')
    .max(100, 'Maximum 100 characters'),
  contactStatus: Yup.mixed<contactStatusEnum>().oneOf(
    Object.values(contactStatusEnum),
  ),
  email: Yup.string().email('Invalid email').required('Required'),
  professionalPhone: Yup.string()
    .required('Required')
    .matches(/^\d{10}$/, 'Must be a valid phone number'),
  fax: Yup.string()
    .optional()
    .matches(/^\d{10}$/, 'Must be a valid fax number'),
  mutuelleId: Yup.string()
    .required('Required')
    .max(50, 'Maximum 50 characters'),
  toTransmit: Yup.boolean().required('Required'),
  teletransmissionDate: Yup.string()
    .required('Required')
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
  activeFrom: Yup.string()
    .required('Required')
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
  activeTo: Yup.string()
    .optional()
    .matches(/^\d{2}-\d{2}-\d{4}$/, 'Must be a valid date (DD-MM-YYYY)'),
})

export const patientCoverageSchema = Yup.object().shape({
  sex: Yup.string().required('Merci de choisir une civilité'),
  nom: Yup.string().required('Merci de renseigner le nom du patient'),
  prenom: Yup.string().required('Merci de renseigner le prénom du patient'),
  dateNaissance: Yup.string().optional(),
  assureNom: Yup.string().required('Merci de renseigner le nom de l\'assuré'),
  assurePrenom: Yup.string().required(
    'Merci de renseigner le prénom de l\'assuré',
  ),
  qualite: Yup.string().optional(),
  rang: Yup.string().optional(),
  insNomNaissance: Yup.string().required(
    'Merci de renseigner le nom de naissance',
  ),
  premierPrenom: Yup.string().required('Merci de renseigner le premier prénom'),
  listePrenoms: Yup.string().required(
    'Merci de renseigner la liste des prénoms',
  ),
  lieuNaissance: Yup.string().required(
    'Merci de renseigner le lieu de naissance',
  ),
  ins: Yup.string().optional(),
  oid: Yup.string().optional(),
  natureAssurance: Yup.string().optional(),
  exoneration: Yup.string().optional(),
  amo: Yup.string().optional(),
  amc: Yup.string().optional(),
  debutAmo: Yup.date()
    .max(new Date(), 'Date cannot be in the future')
    .optional(),
  finAmo: Yup.date().min(new Date(), 'Date cannot be in the past').optional(),
  debutAmc: Yup.date()
    .max(new Date(), 'Date cannot be in the future')
    .optional(),
  finAmc: Yup.date().min(new Date(), 'Date cannot be in the past').optional(),
  numeroAdherent: Yup.number().positive().integer().optional(),
  gestion: Yup.boolean().optional(),
  parcoursSoins: Yup.mixed<pathwayhealth>().oneOf(Object.values(pathwayhealth)),
  medecinTraitantDeclare: Yup.object().required('Medecin traitant is required'),
  medecinTraitant: Yup.object().required('Medecin traitant is required'),
})

export const patientFormSchema = Yup.object().shape({
  sex: Yup.string().required('La civilité est obligatoire'),
  firstName: Yup.string().required('Le prénom est obligatoire'),
  lastName: Yup.string().required('Le nom est obligatoire'),
  motherMaidenName: Yup.string().optional(),
  birthDate: Yup.date()
    .optional()
    .nullable()
    .typeError('Veuillez saisir une date valide')
    .min(
      new Date(1900, 0, 1),
      'La date de naissance doit être postérieure à 1900',
    )
    .max(
      new Date(new Date().getTime() + 86400000),
      'La date de naissance ne peut pas être postérieure à aujourd\'hui',
    ),

  phoneNumber: Yup.string()
    .optional()
    .nullable()
    .test(
      'phone',
      'Le numéro de téléphone doit être un numéro français valide',
      function (value) {
        if (!value || value === '') return true

        // Skip French phone validation if patient is foreigner
        const isForeigner = this.parent.isForeigner
        if (isForeigner) return true

        return /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(value)
      },
    ),
  email: Yup.string().optional().email('L\'adresse email n\'est pas valide'),
  address: Yup.string().nullable().optional(),
  birthPlace: Yup.string()
    .optional()
    .nullable()
    .min(2, 'Birth place must be at least 2 characters'),
  birthPlaceDepartment: Yup.string().nullable().optional(),
  ssn: Yup.string()
    .optional()
    .test('valid-ssn', 'Numéro de sécurité sociale invalide', (value) => {
      if (!value) return true // Allow empty values since it's optional
      return true
    }),
  weight: Yup.number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .nullable()
    .optional()
    .test('weight-range', 'Poids trop élevé', (value) => {
      if (value === null || value === undefined) return true
      return value <= 400
    }),
  height: Yup.number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .nullable()
    .optional()
    .test('height-range', 'Taille trop élevée', (value) => {
      if (value === null || value === undefined) return true
      return value <= 300
    }),
  familyDoctor: Yup.object().optional().nullable(),
  comments: Yup.string()
    .optional()
    .max(500, 'Les commentaires ne peuvent pas dépasser 500 caractères'),
  iod: Yup.string().optional(),
  ins: Yup.string().optional(),
  files: Yup.array().optional(),
  isForeigner: Yup.boolean().optional(),
})

export const patientSearchSchema = Yup.object().shape({
  name: Yup.string().optional(),
  lastName: Yup.string().optional(),
  birthDate: Yup.string().optional(),
  ipp: Yup.string().optional(),
  originIpp: Yup.string().optional(),
  sex: Yup.string().optional(),
})

export const MedicalOrderSchema = Yup.object().shape({
  siteId: Yup.number()
    .typeError('Site est obligatoire')
    .required('Site est obligatoire'),
  attendingDoctor: Yup.object().nullable().optional(),
  referringDoctor: Yup.object().nullable().optional(),
  consultingDoctor: Yup.object().nullable().optional(),
  registerDate: Yup.object().optional().nullable(),
  comments: Yup.string().nullable().optional(),
})

export const createQuotationSchema = Yup.object().shape({
  cotation_type: Yup.string().required().oneOf(['CCAM', 'NGAP']),
  cotation: Yup.string().required(),
  modificateur: Yup.string().optional(),
  prix_unitaire: Yup.number().required().positive(),
  code_entente_prealable: Yup.string().nullable().optional(),
  date_entente_prealable: Yup.date().nullable().optional(),
})

export const visitSchema = Yup.object().shape({
  // patientId will be handeled manually in the submit callback, its required but not validated
  patientId: Yup.number().optional(),
  siteId: Yup.number().required('validation.field.required'),
  admissionDate: Yup.date()
    .typeError('Veuillez renseigner la date d\'admission')
    .required('Veuillez renseigner la date d\'admission'),
  dischargeDate: Yup.object().nullable().optional(),
  hospitalService: Yup.string().optional(),
})

export const couvertureFormSchema = Yup.object().shape({
  beneficiaireNom: Yup.string().required('Le nom de l\'assuré est obligatoire'),
  beneficiairePrenom: Yup.string().required(
    'Le prénom de l\'assuré est obligatoire',
  ),
})
export const patientAldSchema = Yup.object().shape({
  code: Yup.string().nullable().optional(),
  dateFin: Yup.date().nullable().optional(),
  etat: Yup.string().nullable().optional(),
  cimCode: Yup.string().nullable().optional(),
  comment: Yup.string().nullable().optional(),
})

export const paymentModeSchema = Yup.object().shape({
  code: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  label: Yup.string().required('Requis').max(100, '100 caractères maximum'),
  type: Yup.mixed<PaymentModeType>().oneOf(
    Object.values(PaymentModeType) as PaymentModeType[],
  ),
  accountingSectionCode: Yup.string()
    .optional()
    .max(255, '255 caractères maximum'),
})

export const billingCompanySchema = Yup.object().shape({
  code: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  label: Yup.string().required('Requis').max(100, '100 caractères maximum'),
  registrationNumber: Yup.string()
    .required('Requis')
    .max(50, '50 caractères maximum'),
  finessNumber: Yup.string()
    .required('Requis')
    .max(50, '50 caractères maximum'),
  taxNumber: Yup.string().required('Requis').max(50, '50 caractères maximum'),
  address: Yup.string().required('Requis').max(255, '255 caractères maximum'),
  postalCode: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  city: Yup.string().required('Requis').max(100, '100 caractères maximum'),
  country: Yup.string().required('Requis').max(100, '100 caractères maximum'),
  siteId: Yup.number().typeError('Requis').required('Requis'),
  bankAccountId: Yup.number().typeError('Requis').required('Requis'),
})

export const bankEstablishmentSchema = Yup.object().shape({
  code: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  label: Yup.string().required('Requis').max(100, '100 caractères maximum'),
})

export const bankAccountSchema = Yup.object().shape({
  code: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  name: Yup.string().required('Requis').max(100, '100 caractères maximum'),
  countryCode: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  bankCode: Yup.string().required('Requis').length(5, '5 caractères'),
  branchCode: Yup.string().required('Requis').length(5, '5 caractères'),
  accountNumber: Yup.string()
    .required('Requis')
    .max(100, '100 caractères maximum'),
  controlKey: Yup.string().required('Requis').length(2, '2 caractères'),
  swiftCode: Yup.string()
    .required('Requis')
    .test(
      'length',
      '8 ou 11 caractères',
      (value) =>
        !!value &&
        (value.replaceAll(/\s+/g, '').length === 8 ||
          value.replaceAll(/\s+/g, '').length === 11),
    ),
  siteIds: Yup.array(Yup.number()).optional(),
})

export const patientPaymentSchema = Yup.object().shape({
  paymentModeId: Yup.number().required('Requis'),
  amount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  comment: Yup.string()
    .nullable()
    .max(255, '255 caractères maximum')
    .optional(),
  // PaymentModeType.CHEQUE case
  chequeNumber: Yup.string().nullable().optional(),
  bankEstablishmentId: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .optional(), // + PaymentModeType.BANK_TRANSFER case
  drawerName: Yup.string().nullable().optional(),
  minimumRequestedDepositDate: Yup.date()
    .transform((value, originalValue) => (!originalValue ? null : value))
    .min(new Date(), 'Impossible de choisir une date dans le passé')
    .max(
      moment().add(365, 'days').toDate(),
      'Impossible de choisir une date d\'encaissement supérieure à 1 an',
    )
    .nullable()
    .optional(),
})

export const chequeDepositSchema = Yup.object().shape({
  depositReference: Yup.string()
    .required('Requis')
    .max(50, '50 caractères maximum'),
  chequeIds: Yup.array().min(1, 'Requis').of(Yup.number()).required('Requis'),
})

export const quoteSchema = Yup.object().shape({
  patientId: Yup.number().required('Requis'),
  attendingDoctorId: Yup.number().nullable().optional(),
  attendingDoctorSiteId: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .optional(),
})

export const dunningNoticeConfigSchema = Yup.object().shape({
  daysLevel1: Yup.number()
    .integer('Invalide')
    .typeError('Requis')
    .required('Requis')
    .moreThan(0, 'Invalide'),
  daysLevel2: Yup.number()
    .integer('Invalide')
    .typeError('Requis')
    .required('Requis')
    .moreThan(Yup.ref('daysLevel1'), 'Doit être supérieur au niveau 1'),
  daysLevel3: Yup.number()
    .integer('Invalide')
    .typeError('Requis')
    .required('Requis')
    .moreThan(Yup.ref('daysLevel2'), 'Doit être supérieur au niveau 2'),
  content1: Yup.string().required('Requis'),
  content2: Yup.string().required('Requis'),
  content3: Yup.string().required('Requis'),
})

export const organismPaymentSchema = Yup.object().shape({
  paymentModeId: Yup.number().required('Requis'),
  date: Yup.date()
    .max(new Date(), 'Invalide')
    .typeError('Requis')
    .required('Requis'),
  comment: Yup.string().max(255, '255 caractères maximum').optional(),
  bankEstablishmentId: Yup.number().typeError('Requis').required('Requis'),
  // PaymentModeType.CHEQUE case
  chequeNumber: Yup.string().nullable().optional(),
  drawerName: Yup.string().nullable().optional(),
})

export const cashRegisterSchema = Yup.object().shape({
  code: Yup.string().required('Requis').max(10, '10 caractères maximum'),
  name: Yup.string().required('Requis').max(100, '100 caractères maximum'),
  location: Yup.string().required('Requis').max(255, '255 caractères maximum'),
  active: Yup.boolean(),
  balance: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  siteId: Yup.number().typeError('Requis').required('Requis'),
})

export const cashRegisterTransactionSchema = Yup.object().shape({
  paymentModeId: Yup.number().required('Requis'),
  amount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  description: Yup.string().max(255, '255 caractères maximum').optional(),
  transactionType: Yup.mixed().oneOf(['DEBIT', 'CREDIT']).required('Requis'),
})

export const openCashRegisterSchema = Yup.object().shape({
  cashAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  checkAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  voucherAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  description: Yup.string().max(255, '255 caractères maximum').optional(),
})

export const closeCashRegisterSchema = Yup.object().shape({
  cashAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  checkAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  voucherAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  transferAmount: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  description: Yup.string().max(255, '255 caractères maximum').optional(),
})

export const CreateAcquisitionProtocolSchema = Yup.object().shape({
  title: Yup.string().required('validation.field.required'),
  description: Yup.string().nullable().optional(),
  pdfUrl: Yup.string().nullable().optional(),
  siteIds: Yup.array().min(1, 'validation.field.required'),
})
export const createMssProcessedEmailStepSchemas = (
  attachmentsLength: number,
) => {
  const schemas: Yup.ObjectSchema<any>[] = []

  if (attachmentsLength > 0) {
    schemas.push(
      Yup.object({
        attachments: Yup.array().of(
          Yup.object().shape({
            date: Yup.date()
              .nullable()
              .required('Requis')
              .max(
                new Date(),
                'La date doit être égale ou antérieure à aujourd\'hui',
              )
              .transform((value, originalValue) =>
                originalValue === '' ? null : value,
              )
              .required('Requis'),
            type: Yup.mixed<FileTypeEnum>()
              .oneOf(Object.values(FileTypeEnum), 'Requis')
              .required('Requis'),
          }),
        ),
      }),
    )
  }

  schemas.push(
    Yup.object({
      status: Yup.mixed<MssRequestStatus>()
        .oneOf(Object.values(MssRequestStatus) as MssRequestStatus[], 'Requis')
        .required('Requis'),
      priority: Yup.mixed<MssRequestPriority | ''>()
        .oneOf([...Object.values(MssRequestPriority), ''], 'Requis')
        .optional(),
      priorityComment: Yup.string()
        .max(255, '255 caractères maximum')
        .when('priority', {
          is: MssRequestPriority.SPECIFIC,
          then: Yup.string().required('Requis pour une priorité spécifique'),
          otherwise: Yup.string().optional(),
        }),
      examIds: Yup.array().min(1, 'Requis').of(Yup.number()).required('Requis'),
      functionalUnitId: Yup.number().required('Requis'),
      referringDoctorId: Yup.number().required('Requis'),
      siteId: Yup.number().required('Requis'),
      indication: Yup.string()
        .max(1000, '1000 caractères maximum')
        .when([], {
          is: () => attachmentsLength === 0,
          then: Yup.string().required('Requis'),
          otherwise: Yup.string().optional(),
        }),
    }),
    Yup.object({
      patientId: Yup.number().required(
        'La sélection d\'un patient est obligatoire. Crééz un patient si nécessaire.',
      ),
    }),
  )

  return schemas
}

export const compoundSchema = Yup.object().shape({
  name: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  description: Yup.string().nullable().optional(),
  type: Yup.mixed<CompoundType>()
    .oneOf(Object.values(CompoundType) as CompoundType[])
    .required('Requis'),
  coldKitId: Yup.string()
    .nullable()
    .when('type', {
      is: CompoundType.TO_PREPARE,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  isotopeIds: Yup.array(Yup.string())
    .nullable()
    .when('type', {
      is: CompoundType.PURE_ISOTOPE,
      then: (schema) => schema.min(1, 'Requis'),
      otherwise: (schema) => schema,
    })
    .when('type', {
      is: CompoundType.TO_PREPARE,
      then: (schema) => schema.min(1, 'Requis'),
      otherwise: (schema) => schema,
    }),
  enforceEluateMaxAge: Yup.boolean().optional(),
  eluateMaxAgeMinutes: Yup.number()
    .integer('Doit être un entier')
    .min(1, 'Minimum 1 minute')
    .when('enforceEluateMaxAge', {
      is: true,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  enforcePreparationMaxAge: Yup.boolean().optional(),
  preparationMaxAgeMinutes: Yup.number()
    .integer('Doit être un entier')
    .min(1, 'Minimum 1 minute')
    .when('enforcePreparationMaxAge', {
      is: true,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
})

export const isotopeSchema = Yup.object().shape({
  code: Yup.string()
    .max(10, '10 caractères maximum')
    .required('Requis'),
  name: Yup.string()
    .max(30, '30 caractères maximum')
    .required('Requis'),
  massNumber: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .typeError('Requis')
    .integer('Doit être un entier')
    .min(1, 'Doit être supérieur à 0')
    .max(999, 'Trois chiffres maximum')
    .required('Requis'),
  atomicNumber: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .typeError('Requis')
    .integer('Doit être un entier')
    .min(1, 'Doit être supérieur à 0')
    .max(999, 'Trois chiffres maximum')
    .required('Requis'),
  emissions: Yup.array()
    .of(
      Yup.mixed<IsotopeEmissionType>().oneOf(
        Object.values(IsotopeEmissionType),
        'Valeur invalide'
      )
    )
    .min(1, 'Au moins une émission')
    .required('Requis'),
  physicalHalfLifeS: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .typeError('Requis')
    .integer('Doit être un entier')
    .min(0, 'Doit être supérieur ou égal à 0')
    .required('Requis'),
  peakEnergyKev: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .typeError('Requis')
    .min(0, 'Doit être supérieur ou égal à 0')
    .required('Requis'),
  meanEnergyKev: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .typeError('Requis')
    .min(0, 'Doit être supérieur ou égal à 0')
    .required('Requis'),
  usages: Yup.array()
    .of(
      Yup.mixed<IsotopeUsage>().oneOf(
        Object.values(IsotopeUsage),
        'Valeur invalide'
      )
    )
    .min(1, 'Au moins un usage')
    .required('Requis'),
  notes: Yup.string()
    .max(500, '500 caractères maximum')
    .nullable()
    .optional(),
})

const optionalTrimmedString = () =>
  Yup.string()
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .nullable()
    .optional()

export const supplierSchema = Yup.object().shape({
  name: Yup.string().max(200, '200 caractères maximum').required('Requis'),
  type: Yup.mixed<SupplierType>()
    .oneOf(Object.values(SupplierType) as SupplierType[])
    .required('Requis'),
  siret: Yup.string().required('Requis').length(14, '14 caractères'),
  address: Yup.string().nullable().optional(),
  contacts: Yup.array().of(
    Yup.object({
      email: Yup.string()
        .email('Email invalide')
        .max(200, '200 caractères maximum')
        .nullable()
        .optional(),
      phone: Yup.string()
        .max(40, '40 caractères maximum')
        .test('phone-format', 'Format invalide', (value) => {
          if (value == null || value === '') return true
          return /^\+?\d+$/.test(value)
        })
        .nullable()
        .optional(),
      name: Yup.string()
        .max(120, '120 caractères maximum')
        .nullable()
        .optional(),
      type: Yup.mixed<SupplierContactType>()
        .oneOf(Object.values(SupplierContactType) as SupplierContactType[])
        .required('Requis'),
    }).test(
      'at-least-one-contact-field',
      'Au moins un champ parmi email, téléphone et nom',
      (value) => {
        if (!value) return false
        const { email, phone, name } = value
        return Boolean(email || phone || name)
      },
    ),
  ),
  paymentMethods: Yup.array()
    .of(
      Yup.mixed<SupplierPaymentMethod>().oneOf(
        Object.values(SupplierPaymentMethod) as SupplierPaymentMethod[],
      ),
    )
    .optional(),
  notes: Yup.string().nullable().optional(),
  cyclotron: Yup.boolean().optional(),
  customerSites: Yup.array()
    .of(
      Yup.object({
        customerNumber: Yup.string()
          .max(100, '100 caractères maximum')
          .required('Requis'),
        siteId: Yup.number().required('Requis'),
      }),
    )
    .optional(),
  productionSites: Yup.array()
    .of(Yup.string().max(100, '100 caractères maximum'))
    .optional(),
  countryCode: optionalTrimmedString().test(
    'countryCode-max',
    '10 caractères maximum',
    (v) => !v || v.length <= 10,
  ),
  bankCode: optionalTrimmedString().test(
    'bankCode-length',
    '5 caractères',
    (v) => !v || v.length === 5,
  ),
  branchCode: optionalTrimmedString().test(
    'branchCode-length',
    '5 caractères',
    (v) => !v || v.length === 5,
  ),
  accountNumber: optionalTrimmedString().test(
    'accountNumber-max',
    '100 caractères maximum',
    (v) => !v || v.length <= 100,
  ),
  controlKey: optionalTrimmedString().test(
    'controlKey-length',
    '2 caractères',
    (v) => !v || v.length === 2,
  ),
  swiftCode: optionalTrimmedString().test(
    'swift-length',
    '8 ou 11 caractères',
    (v) => {
      if (!v) return true
      const s = v.replaceAll(/\s+/g, '')
      return s.length === 8 || s.length === 11
    },
  ),
})

export const storageLocationSchema = Yup.object().shape({
  name: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  roomId: Yup.number().required('Requis'),
  type: Yup.mixed<StorageType>()
    .oneOf(Object.values(StorageType) as StorageType[])
    .required('Requis'),
  capacityL: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .min(0, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  setpointC: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional(),
  rangeMinC: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional(),
  rangeMaxC: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional()
    .test('range-order', 'Doit être supérieur au Min', function (value) {
      const { rangeMinC } = this.parent
      if (
        value === null ||
        value === undefined ||
        rangeMinC === null ||
        rangeMinC === undefined
      )
        return true
      return value > rangeMinC
    }),
  alarmLowC: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional(),
  alarmHighC: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional()
    .test(
      'alarm-order',
      'Doit être supérieur à l\'Alarme basse',
      function (value) {
        const { alarmLowC } = this.parent
        if (
          value === null ||
          value === undefined ||
          alarmLowC === null ||
          alarmLowC === undefined
        )
          return true
        return value > alarmLowC
      },
    ),
  monitoring: Yup.mixed<MonitoringMethod>()
    .oneOf(Object.values(MonitoringMethod) as MonitoringMethod[])
    .nullable()
    .optional(),
  notes: Yup.string().nullable().optional(),
})

export const productFamilySchema = Yup.object().shape({
  name: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  code: Yup.string().max(50, '50 caractères maximum').nullable().optional(),
  description: Yup.string().nullable().optional(),
  parentFamilyId: Yup.string().nullable().optional(),
  color: Yup.string().max(16, '16 caractères maximum').nullable().optional(),
  icon: Yup.string().max(32, '32 caractères maximum').nullable().optional(),
})

const productCatalogValidationSchema = Yup.object({
  rules: Yup.array().of(
    Yup.object({
      role: Yup.string().required('Requis'),
      operator: Yup.mixed<'AND' | 'OR'>()
        .oneOf(['AND', 'OR'])
        .required('Requis'),
      memberIds: Yup.array().of(Yup.number()).min(1, 'Requis'),
    }),
  ),
  groupOperator: Yup.mixed<'AND' | 'OR' | null>()
    .nullable()
    .when('rules', (rules, schema) => {
      const hasMultipleRules = rules && rules.length > 1
      return hasMultipleRules
        ? schema.oneOf(['AND', 'OR'], 'Requis').required('Requis')
        : schema.oneOf(['AND', 'OR', null]).nullable()
    }),
})

const isValidDateString = (value?: string | null) =>
  !value ? true : !isNaN(new Date(value).getTime())

export const productCatalogPriceSchema = Yup.object().shape({
  priceExclVat: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .min(0, 'Doit être supérieur à 0')
    .test('is-decimal', 'Format non valide', (value) =>
      /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
    ),
  vatRate: Yup.number()
    .required('Requis')
    .typeError('Requis')
    .moreThan(0, 'Format invalide')
    .lessThan(1, 'Format invalide')
    .test(
      'is-decimal',
      'Format non valide',
      (value) => value != null && /^\d+(\.\d{1,4})?$/.test(String(value)),
    ),
  startAt: Yup.string()
    .nullable()
    .optional()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide')
    .test('is-valid-date', 'Date invalide', (value) =>
      isValidDateString(value),
    ),
  endAt: Yup.string()
    .required('Requis')
    .test('is-valid-date', 'Date invalide', (value) => isValidDateString(value))
    .test(
      'end-after-start',
      'La date de fin doit être après la date de début',
      function (endAt) {
        const { startAt } = this.parent
        if (
          !startAt ||
          !endAt ||
          !isValidDateString(startAt) ||
          !isValidDateString(endAt)
        ) {
          return true
        }
        return startAt <= endAt
      },
    ),
})

export const productCatalogSchema = Yup.object().shape({
  supplierId: Yup.string().required('Requis'),
  productCode: Yup.string()
    .max(100, '100 caractères maximum')
    .required('Requis'),
  name: Yup.string().max(200, '200 caractères maximum').required('Requis'),
  genericName: Yup.string().required('Requis'),
  supplyLeadTimeDays: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .min(1, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  maxDivision: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .min(1, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  type: Yup.mixed<ProductType>()
    .oneOf(Object.values(ProductType) as ProductType[])
    .required('Requis'),
  generatorParentIsotopeId: Yup.string().when('type', {
    is: ProductType.GENERATOR,
    then: Yup.string()
      .required('Requis')
      .notOneOf(
        [Yup.ref('generatorDaughterIsotopeId')],
        'Doit être différent de l\'isotope fils',
      ),
    otherwise: Yup.string().nullable().optional(),
  }),
  generatorDaughterIsotopeId: Yup.string().when('type', {
    is: ProductType.GENERATOR,
    then: Yup.string()
      .required('Requis')
      .notOneOf(
        [Yup.ref('generatorParentIsotopeId')],
        'Doit être différent de l\'isotope père',
      ),
    otherwise: Yup.string().nullable().optional(),
  }),
  compoundId: Yup.string().nullable().optional(),
  packageUom: Yup.mixed<ProductPackageUom>()
    .oneOf(Object.values(ProductPackageUom) as ProductPackageUom[])
    .nullable()
    .optional(),
  unitsPerPack: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .integer('Doit être un entier')
    .min(1, 'Doit être supérieur à 0')
    .nullable()
    .when('packageUom', {
      is: ProductPackageUom.PACK,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  gtinEan: Yup.string().max(32, '32 caractères maximum').nullable().optional(),
  primaryFamilyId: Yup.string().nullable().optional(),
  requiresColdChain: Yup.boolean(),
  inUseStabilityMinCold: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .integer('Doit être un entier')
    .min(0, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  inUseStabilityMinRoom: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .integer('Doit être un entier')
    .min(0, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  excursionOutOfColdMaxMin: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .integer('Doit être un entier')
    .min(0, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  notes: Yup.string().nullable().optional(),
  prices: Yup.array()
    .of(productCatalogPriceSchema)
    .test(
      'not-only-future-startAt',
      'Au moins une période de prix effective aujourd\'hui',
      (prices) => {
        if (!prices) {
          return false
        }
        const currentPrice = getCurrentPrice(
          prices.map(({ startAt, endAt, vatRate, priceExclVat }) => ({
            startAt: startAt ?? undefined,
            endAt: endAt ?? '',
            vatRate: vatRate ?? 0,
            priceExclVat: priceExclVat ?? 0,
          })),
        )
        return !!currentPrice
      },
    ),
  extraCost: Yup.number()
    .nullable()
    .optional()
    .typeError('Invalide')
    .min(0, 'Doit être supérieur à 0')
    .test('is-decimal', 'Format non valide', (value) => {
      if (value === null || value === undefined) return true
      return /^\d+(\.\d{1,2})?$/.test(value.toString())
    }),
  examIds: Yup.array().of(Yup.number()).nullable().optional(),
  orderableIfExamAssociated: Yup.boolean(),
  validation: productCatalogValidationSchema,
})

export const getProductOrderSchema = (step: ProductOrderFormStep) => {
  switch (step) {
    case ProductOrderFormStep.Cart:
      return Yup.object({
        supplierId: Yup.string().required('Requis'),
        lineItems: Yup.array()
          .of(
            Yup.object({
              productId: Yup.string().required('Requis'),
              quantity: Yup.number()
                .typeError('Format invalide')
                .integer('Format invalide')
                .positive('Format invalide')
                .required('Requis'),
            }),
          )
          .min(1, 'Au moins un produit doit être ajouté au panier'),
        extraCost: optionalDecimalField().min(0, 'Format invalide'),
      })
    case ProductOrderFormStep.Info:
      return Yup.object({
        shippingAddressId: Yup.number()
          .typeError('Format invalide')
          .required('Requis'),
        siteId: Yup.number().typeError('Format invalide').required('Requis'),
        deliveryInstructions: Yup.string().optional(),
        lineItems: Yup.array().of(
          Yup.object({
            productId: Yup.string().required(),
            desiredDeliveryDate: Yup.string()
              .nullable()
              .optional()
              .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide')
              .test('is-valid-date', 'Date invalide', (value) => {
                if (!value) return true
                return !isNaN(new Date(value).getTime())
              })
              .test('is-future', 'La date doit être dans le futur', (value) => {
                if (!value) return true
                return new Date(value) >= new Date()
              }),
            isStandardDelay: Yup.boolean().optional(),
            hasGePurchaseOrderAttachment: Yup.boolean().optional(),
            plannedDates: Yup.array()
              .of(
                Yup.object({
                  date: Yup.string()
                    .required('Date requise')
                    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide')
                    .test(
                      'is-future',
                      'La date doit être dans le futur',
                      (value) => {
                        if (!value) return true
                        return (
                          new Date(value) >= new Date(new Date().toDateString())
                        )
                      },
                    ),
                  quantity: Yup.number()
                    .required('Quantité requise')
                    .min(1, 'Minimum 1'),
                }),
              )
              .optional(),
          }).test('delivery-rule', function (item) {
            if (!item) {
              return true
            }
            const context = this as Yup.TestContext & {
              from: { value: Record<string, any> }[]
              options: {
                context: {
                  selectedProducts: ProductCatalogDto[]
                }
              }
            }
            const product = context.options.context.selectedProducts.find(
              ({ id }) => id === item.productId,
            )
            if (product?.type === ProductType.GENERATOR) {
              if (!item.hasGePurchaseOrderAttachment) {
                return this.createError({
                  message: 'Veuillez fournir le bon commande',
                })
              }
              const plannedTotal = (item.plannedDates || []).reduce(
                (sum, d) => sum + (d.quantity || 0),
                0,
              )
              const quantity =
                context.from?.[1]?.value?.lineItems?.find(
                  ({ productId }) => productId === item.productId,
                )?.quantity || 0
              if (plannedTotal < quantity) {
                return this.createError({
                  message: `${plannedTotal}/${quantity} générateur${
                    plannedTotal > 1 ? 's' : ''
                  } planifié${plannedTotal > 1 ? 's' : ''}`,
                })
              }
              if (plannedTotal > quantity) {
                return this.createError({
                  message: `Trop de générateurs planifiés (${plannedTotal}/${quantity})`,
                })
              }
              return true
            }
            if (!item.desiredDeliveryDate && !item.isStandardDelay) {
              return this.createError({
                message:
                  'Veuillez choisir une date ou confirmer le délai standard',
              })
            }
            return true
          }),
        ),
        supplierContactId: Yup.string().required('Requis'),
      })
    default:
      return Yup.object({})
  }
}

export const injectionSiteSchema = Yup.object().shape({
  code: Yup.string().max(50, '50 caractères maximum').required('Requis'),
  bodyPart: Yup.string().max(80, '80 caractères maximum').nullable().optional(),
  side: Yup.mixed<Side>()
    .oneOf(Object.values(Side) as Side[])
    .required('Requis'),
})

export const wasteContainerSchema = Yup.object().shape({
  code: Yup.string().max(32, '32 caractères maximum').nullable().optional(),
  label: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  type: Yup.mixed<WasteType>()
    .oneOf(Object.values(WasteType) as WasteType[])
    .required('Requis'),
  activityLevel: Yup.mixed<ActivityLevel>()
    .oneOf(Object.values(ActivityLevel) as ActivityLevel[])
    .required('Requis'),
  notes: Yup.string().nullable().optional(),
  storageLocationId: Yup.string().nullable().optional(),
  isotopeIds: Yup.array().of(Yup.string()).when('activityLevel', {
    is: (val: string) => val === ActivityLevel.LOW || val === ActivityLevel.HIGH,
    then: (schema) => schema.min(1, 'Au moins un isotope requis').required('Requis'),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  isInfectious: Yup.boolean().optional(),
  location: Yup.string()
    .max(120, '120 caractères maximum')
    .nullable()
    .optional(),
  tareKg: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .min(0, 'Doit être supérieur à 0')
    .nullable()
    .optional(),
  volumeL: optionalDecimalField().min(0, 'Doit être supérieur à 0'),
})

export const hotLabEquipmentSchema = Yup.object().shape({
  type: Yup.mixed<HotLabEquipmentType>()
    .oneOf(Object.values(HotLabEquipmentType) as HotLabEquipmentType[])
    .required('Requis'),
  name: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  serial: Yup.string().max(120, '120 caractères maximum').nullable().optional(),
  manufacturer: Yup.string()
    .max(120, '120 caractères maximum')
    .nullable()
    .optional(),
  model: Yup.string().max(120, '120 caractères maximum').nullable().optional(),
  installedAt: Yup.date()
    .transform((value, originalValue) => (!originalValue ? null : value))
    .nullable()
    .optional(),
  location: Yup.string()
    .max(120, '120 caractères maximum')
    .nullable()
    .optional(),
  notes: Yup.string().nullable().optional(),
  storageLocationIds: Yup.array()
    .of(Yup.string().nullable())
    .nullable()
    .optional(),
  hasPeriodicQualityChecks: Yup.boolean().nullable().optional(),
  internalCheckIntervalDays: optionalDecimalField().min(
    0,
    'Doit être supérieur à 0',
  ),
  externalCheckIntervalDays: optionalDecimalField().min(
    0,
    'Doit être supérieur à 0',
  ),
  externalOrganizationId: Yup.string().nullable().optional(),
})

export const visualQualityControlSchema = Yup.object().shape({
  label: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  type: Yup.mixed<VisualQualityControlType>()
    .oneOf(
      Object.values(VisualQualityControlType) as VisualQualityControlType[],
    )
    .required('Requis'),
})

export const customQualityControlSchema = Yup.object().shape({
  code: Yup.string().max(50, '50 caractères maximum').required('Requis'),
  label: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  type: Yup.mixed<CustomQualityControlType>()
    .oneOf(Object.values(CustomQualityControlType))
    .required('Requis'),
  options: Yup.array()
    .of(
      Yup.object().shape({
        code: Yup.string().max(50, '50 caractères maximum').required('Requis'),
        label: Yup.string()
          .max(120, '120 caractères maximum')
          .required('Requis'),
      }),
    )
    .min(2, 'Au moins 2 options sont requises')
    .when('type', {
      is: CustomQualityControlType.LIST,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.strip(),
    }),
  expectedOptionCode: Yup.string()
    .max(50, '50 caractères maximum')
    .nullable()
    .when('type', {
      is: CustomQualityControlType.LIST,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.strip(),
    }),
  expected: Yup.boolean().when('type', {
    is: CustomQualityControlType.BOOLEAN,
    then: (schema) => schema.required('Requis').typeError('Requis'),
    otherwise: (schema) => schema.strip(),
  }),
  expectedValue: Yup.string()
    .max(255, '255 caractères maximum')
    .nullable()
    .when('type', {
      is: CustomQualityControlType.TEXT,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.strip(),
    }),
  minimum: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('type', {
      is: CustomQualityControlType.NUMERIC,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.strip(),
    }),
  maximum: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('type', {
      is: CustomQualityControlType.NUMERIC,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.strip(),
    }),
  unit: Yup.string().max(50, '50 caractères maximum').nullable().optional(),
  equipmentTypes: Yup.array()
    .of(Yup.mixed<HotLabEquipmentType>().oneOf(Object.values(HotLabEquipmentType)))
    .optional(),
}).test('numeric-requires-min-or-max', 'Au moins un minimum ou maximum est requis', function (value) {
  if (value.type !== CustomQualityControlType.NUMERIC || value.minimum != null || value.maximum != null) {
    return true
  }
  return this.createError({ path: 'minimum', message: 'Au moins un minimum ou maximum est requis' })
})

export const qualityControlAssignmentSchema = Yup.object().shape({
  compoundId: Yup.string().required('Requis'),
  customQualityControlIds: Yup.array().of(Yup.string()).nullable().optional(),
  colorQualityControlId: Yup.string().nullable().optional(),
  appearanceQualityControlId: Yup.string().nullable().optional(),
  radiochemicalPurityPercent: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .min(0, 'Doit être supérieur ou égal à 0')
    .max(100, 'Doit être inférieur ou égal à 100')
    .nullable()
    .optional(),
  radionuclidicPurityPercent: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .min(0, 'Doit être supérieur ou égal à 0')
    .max(100, 'Doit être inférieur ou égal à 100')
    .nullable()
    .optional(),
  phMinimum: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional(),
  phMaximum: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .optional(),
})

export const activityLimitSchema = Yup.object().shape({
  isotopeId: Yup.string().required('Requis'),
  siteId: Yup.number().required('Requis'),
  maxActivityMbq: Yup.number()
    .typeError('Requis')
    .required('Requis')
    .min(0, 'Doit être supérieur ou égal à 0'),
  authorizationNumber: Yup.string().nullable().optional(),
  notes: Yup.string().nullable().optional(),
  isActive: Yup.boolean().optional(),
})

export const compoundProductionStepSchema = Yup.object().shape({
  code: Yup.string().max(50, '50 caractères maximum').required('Requis'),
  label: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  type: Yup.mixed<CompoundProductionStepType>()
    .oneOf(Object.values(CompoundProductionStepType))
    .required('Requis'),
  unit: Yup.string().max(50, '50 caractères maximum').nullable().optional(),
  expectedValue: Yup.string()
    .max(255, '255 caractères maximum')
    .nullable()
    .when('type', {
      is: CompoundProductionStepType.TEXT,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.strip(),
    }),
  expected: Yup.boolean().when('type', {
    is: CompoundProductionStepType.BOOLEAN,
    then: (schema) => schema.required('Requis').typeError('Requis'),
    otherwise: (schema) => schema.strip(),
  }),
  dilutionVolumeMl: Yup.number()
    .max(999.99, 'Max 999.99 mL')
    .when('type', {
      is: CompoundProductionStepType.DILUTION,
      then: (schema) => schema.required('Requis').min(0.01, 'Minimum 0.01 mL'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  timerDurationSeconds: Yup.number()
    .integer('Valeur entière requise')
    .when('type', {
      is: CompoundProductionStepType.TIMER,
      then: (schema) => schema.required('Requis').min(1, 'Minimum 1 seconde'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
  comment: Yup.string().max(500, '500 caractères maximum').nullable().optional(),
  siteId: Yup.number().required('Requis'),
})

export const compoundProductionStepAssignmentSchema = Yup.object().shape({
  compoundId: Yup.string().required('Requis'),
  stepIds: Yup.array()
    .of(Yup.string())
    .min(1, 'Au moins une étape est requise')
    .required('Requis'),
})

export const getPreparationDocumentSchema = (editing: boolean) =>
  Yup.object().shape({
    compoundId: Yup.string().required('Requis'),
    siteId: Yup.number().required('Requis'),
    label: Yup.string().max(120, '120 caractères maximum').required('Requis'),
    file: Yup.mixed<File>().when([], {
      is: () => !editing,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.optional(),
    }),
  })

export const activimeterDetailSchema = Yup.object().shape({
  equipmentId: Yup.string().required('Requis'),
  path: Yup.string().nullable().optional(),
  electronicZero: optionalDecimalField(),
  voltageMin: optionalDecimalField(),
  voltageMax: optionalDecimalField(),
  testWithCobalt57: Yup.boolean().optional(),
  testWithCesium137: Yup.boolean().optional(),
  testWithBarium133: Yup.boolean().optional(),
  backgroundWithoutSampleHolderMbq: optionalDecimalField(),
  backgroundWithSampleHolderMbq: optionalDecimalField(),
})

export const activimeterCalibrationSchema = Yup.object().shape({
  equipmentId: Yup.string().required('Requis'),
  isotopeId: Yup.string().required('Requis'),
  value: Yup.string().required('Requis'),
  forbidElutionCreation: Yup.boolean().optional(),
  forbidMrpCreation: Yup.boolean().optional(),
  forbidSyringeCreation: Yup.boolean().optional(),
  forbidSingleDoseCreation: Yup.boolean().optional(),
  forbidInhalationCreation: Yup.boolean().optional(),
})

export const functionalUnitSchema = Yup.object().shape({
  name: Yup.string().required('Le nom est requis'),
  number: Yup.string().required('Le numéro est requis'),
  siteId: Yup.number().required('Le site est requis'),
  label: Yup.string(),
  serviceManagerName: Yup.string().optional(),
  careType: Yup.mixed<CareTypeEnum>()
    .oneOf(Object.values(CareTypeEnum))
    .required('Le type de soins est requis'),
  isExecutingUnit: Yup.boolean().required(),
  isRequestingUnit: Yup.boolean().required(),
  functionType: Yup.mixed<HospitalUnitType>()
    .oneOf(Object.values(HospitalUnitType))
    .optional(),
  functionSubType: Yup.string().nullable().optional(),
  details: Yup.object()
    .nullable()
    .when('functionType', {
      is: HospitalUnitType.MEDICAL,
      then: (schema) =>
        schema.shape({
          authorizationDate: Yup.date().nullable().optional(),
          authorizationEndDate: Yup.date().nullable().optional(),
          authorizationNumber: Yup.string().optional(),
          authorizationType: Yup.string().optional().nullable(),
          dedicatedBedsCount: Yup.number()
            .min(0, 'Le nombre de lits doit être positif')
            .optional()
            .nullable(),
          dedicatedBedType: Yup.string().nullable().optional(),
          hospitalizationMode: Yup.string().nullable().optional(),
        }),
      otherwise: (schema) => schema.nullable().optional(),
    }),
})

export const insiSearchSchema = Yup.object().shape({
  beneficiaireNom: Yup.string().required(
    'Le nom du bénéficiaire est obligatoire',
  ),
  beneficiaireSexe: Yup.string().required('Le sexe est obligatoire'),
  beneficiaireDateNaissance: Yup.string().required(
    'La date de naissance est obligatoire',
  ),
  beneficiaireLieu: Yup.string().notRequired(),
  beneficiairePrenoms: Yup.array()
    .of(Yup.string().required('Le prénom est requis'))
    .min(1, 'Au moins un prénom est requis')
    .required(),
  beneficiaireRang: Yup.string().notRequired(),
  beneficiaireNumSecu: Yup.string().notRequired(),
  assureNumSecu: Yup.string().notRequired(),
  numeroSerieCV: Yup.string().notRequired(),
})

export const productOrderAcknowledgmentSchema = Yup.object().shape({
  file: Yup.mixed<File>().required('Requis'),
  supplierOrderNumber: Yup.string().nullable().optional(),
})

export const productOrderPaymentSchema = Yup.object().shape({
  paymentDate: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide')
    .test('is-valid-date', 'Date invalide', (value) => {
      if (!value) {
        return true
      }
      return !isNaN(new Date(value).getTime())
    })
    .test('is-past-or-present', 'Ne doit pas être future', (value) => {
      if (!value) {
        return true
      }
      return new Date(value) <= new Date()
    })
    .required('Requis'),
  markAsPaid: Yup.boolean().required(),
  file: Yup.mixed<File>().nullable().optional(),
})

export const productOrderCancellationSchema = Yup.object().shape({
  supplierContactId: Yup.string().when('$productOrder', {
    is: (productOrder: ProductOrderDto | undefined) =>
      productOrder?.status === ProductOrderStatus.PENDING_ORDER_ACKNOWLEDGMENT,
    then: (schema) => schema.required('Requis'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  reason: Yup.mixed<ProductOrderCancellationReason>()
    .oneOf(
      Object.values(
        ProductOrderCancellationReason,
      ) as ProductOrderCancellationReason[],
    )
    .required('Requis'),
  comment: Yup.string().nullable().optional(),
})

export const stockTransactionSchema = Yup.object().shape({
  productBatchId: Yup.string().required('Requis'),
  storageLocationId: Yup.string().when('reason', {
    is: (reason: StockReason) => reason !== StockReason.EXPIRATION,
    then: (schema) => schema.required('Requis'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  reason: Yup.mixed<StockReason>()
    .oneOf(Object.values(StockReason) as StockReason[])
    .required('Requis'),
  targetStorageLocationId: Yup.string().when('reason', {
    is: StockReason.TRANSFER_OUT,
    then: (schema) => schema.typeError('Requis').required('Requis'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  unitsDelta: Yup.number()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value
    })
    .positive('Doit être > 0')
    .integer('Doit être un entier')
    .typeError('Requis')
    .required('Requis')
    .test('max-units-for-reason', 'Valeur invalide', function (value) {
      const reason = this.parent.reason
      const reasonsWithLimit: StockReason[] = [
        StockReason.ADJUSTMENT_NEGATIVE,
        StockReason.CONSUMPTION,
        StockReason.RETURN_TO_SUPPLIER,
        StockReason.TRANSFER_OUT,
        StockReason.WASTE,
      ]
      const { selectedProductBatch } = this.options.context as {
        selectedProductBatch: ProductBatchDto | null
      }
      if (
        !reasonsWithLimit.includes(reason) ||
        !selectedProductBatch?.storageLocations ||
        value === undefined ||
        value === null
      ) {
        return true
      }
      const storageLocation = selectedProductBatch.storageLocations.find(
        (sl) => sl.storageLocation.id === this.parent.storageLocationId,
      )
      const maxUnits = storageLocation?.units ?? 0
      if (value > maxUnits) {
        return this.createError({ message: `<= ${maxUnits} restant` })
      }
      return true
    }),
  reference: Yup.string()
    .max(120, '120 caractères maximum')
    .nullable()
    .optional(),
  notes: Yup.string().nullable().optional(),
})

export const stockAlertFormSchema = Yup.object().shape({
  thresholdUnits: Yup.number()
    .transform((value) => (isNaN(value) ? null : value))
    .integer('Doit être un entier')
    .min(0, 'Doit être supérieur ou égal à 0')
    .required('Requis'),
  productGenericName: Yup.string().required('Requis'),
})

export const productOrderReceiptNewProductSchema = Yup.object().shape({
  baseProductId: Yup.string().required('Requis'),
  productId: Yup.string().required('Requis'),
})

export const productOrderItemSchema = Yup.object().shape({
  productId: Yup.string().required('Requis'),
  receivedUnits: Yup.number()
    .typeError('Requis')
    .integer('Doit être un entier')
    .min(1, 'Au moins 1')
    .required('Requis'),
  storageLocationId: Yup.string().required('Requis'),
  lotNumber: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  expiryAt: Yup.date().min(new Date(), 'Date future').nullable().optional(),
  activityAtRefMbq: Yup.number().min(0).nullable().optional(),
  activityRefAt: Yup.date().optional().nullable(),
  productionSite: Yup.string().max(120).nullable().optional(),
})

export const productOrderReceiptSchema = (
  step: ProductOrderReceiptFormStep,
) => {
  switch (step) {
    case ProductOrderReceiptFormStep.Products:
      return Yup.object()
        .shape({
          lineItems: Yup.array().of(productOrderItemSchema),
          newProducts: Yup.array().of(
            productOrderItemSchema.shape({
              baseProductId: Yup.string().required('Requis'),
            }),
          ),
        })
        .test(
          'at-least-one-product',
          'Au moins un produit livré',
          function (value) {
            const lineItemsCount = value?.lineItems?.length ?? 0
            const newProductsCount = value?.newProducts?.length ?? 0
            if (lineItemsCount + newProductsCount > 0) {
              return true
            }
            return this.createError({
              path: 'lineItems',
              message: 'Au moins un produit livré',
            })
          },
        )
    case ProductOrderReceiptFormStep.Info:
      return Yup.object().shape({
        receivedAt: Yup.date().required('Requis'),
        courierFullName: Yup.string().nullable().optional(),
        useConnectedMemberAsReceiver: Yup.boolean(),
        receivedByMemberId: Yup.number()
          .nullable()
          .when('useConnectedMemberAsReceiver', {
            is: false,
            then: (schema) => schema.required('Requis'),
            otherwise: (schema) => schema.optional(),
          }),
        useOrderSupplier: Yup.boolean(),
        externalSupplierName: Yup.string()
          .max(200, '200 caractères maximum')
          .nullable()
          .when('useOrderSupplier', {
            is: false,
            then: (schema) => schema.required('Requis'),
            otherwise: (schema) => schema.optional(),
          }),
        externalSupplierAddress: Yup.string()
          .nullable()
          .when('useOrderSupplier', {
            is: false,
            then: (schema) => schema.required('Requis'),
            otherwise: (schema) => schema.optional(),
          }),
        comment: Yup.string().nullable().optional(),
        errorComment: Yup.string().nullable().optional(),
        markOrderAsReceived: Yup.boolean().optional(),
      })
    default:
      return Yup.object().shape({})
  }
}

// Patient Contact Schema
export const patientContactSchema = Yup.object().shape({
  patientId: Yup.number().required('validation.field.required'),
  firstName: Yup.string()
    .required('Le prénom est obligatoire')
    .max(100, 'Maximum 100 caractères'),
  lastName: Yup.string()
    .required('Le nom est obligatoire')
    .max(100, 'Maximum 100 caractères'),
  dateOfBirth: Yup.date()
    .nullable()
    .optional()
    .typeError('Veuillez saisir une date valide')
    .max(new Date(), 'La date de naissance ne peut pas être dans le futur'),
  email: Yup.string().email('Email invalide').nullable().optional(),
  phone: Yup.string().nullable().optional().max(20, 'Maximum 20 caractères'),
  whatsapp: Yup.string().nullable().optional().max(20, 'Maximum 20 caractères'),
  relationshipType: Yup.string()
    .oneOf(
      [
        'SPOUSE',
        'FATHER',
        'MOTHER',
        'SON',
        'DAUGHTER',
        'BROTHER',
        'SISTER',
        'GRANDSON',
        'GRANDDAUGHTER',
        'UNCLE',
        'AUNT',
        'NEPHEW',
        'NIECE',
        'CAREGIVER',
        'OTHER',
      ],
      'Type de relation invalide',
    )
    .required('Le type de relation est obligatoire'),
  isLegalGuardian: Yup.boolean().required(),
  canRequestReport: Yup.boolean().required(),
  is2fa: Yup.boolean().required(),
})

export const preparationLotSchema = Yup.object().shape({
  compoundId: Yup.string().required('Requis'),
  sourceElutionItems: Yup.array()
    .of(
      Yup.object().shape({
        elutionId: Yup.string().required(),
        activityAtRefMbq: Yup.number()
          .required('Requis')
          .typeError('Requis')
          .test('is-decimal', '2 décimales max', (value) =>
            /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
          ),
        elutionVolumeMl: Yup.number()
          .required('Requis')
          .min(0.1, 'Doit être supérieur à 0')
          .test('max-1-decimal', '1 décimale max', value => value == null || Number.isInteger(value * 10))
          .typeError('Requis'),
        activityRefAt: Yup.date().required('Requis').typeError('Date invalide'),
      })
    )
    .min(1, 'Au moins une élution requise')
    .required('Requis'),
  cerclageColor: Yup.string().nullable().max(30, '30 caractères maximum').optional(),
  notes: Yup.string().nullable().max(255, '255 caractères maximum').optional(),
  performedById: Yup.number().required('Requis').typeError('Requis'),
  sourceBatchId: Yup.string().required('Requis'),
  storageLocationId: Yup.string().required('Requis'),
})

export const generatorElutionSchema = Yup.object().shape({
  generatorInstances: Yup.array()
    .of(
      Yup.object().shape({
        generatorInstanceId: Yup.string().required('Requis'),
        activityRefAt: Yup.date().required('Requis').typeError('Date invalide'),
        volumeMl: Yup.number()
          .required('Requis')
          .min(0.1, 'Doit être supérieur à 0')
          .test(
            'max-1-decimal',
            '1 décimale max',
            (value) => value === undefined || Number.isInteger(value * 10),
          )
          .typeError('Requis'),
        activityAtRefMbq: Yup.number()
          .required('Requis')
          .typeError('Requis')
          .test('is-decimal', '2 décimales max', (value) =>
            /^\d+(\.\d{1,2})?$/.test((value ?? '').toString()),
          ),
      })
    )
    .min(1, 'Au moins un générateur requis')
    .required('Requis'),
  notes: Yup.string().nullable().max(255, '255 caractères maximum').optional(),
  performedById: Yup.number().required('Requis').typeError('Requis'),
})

export const workbenchSchema = Yup.object().shape({
  code: Yup.string().max(32, '32 caractères maximum').required('Requis'),
  name: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  hotLabEquipmentIds: Yup.array().of(Yup.string()).nullable().optional(),
  wasteContainerIds: Yup.array().of(Yup.string()).nullable().optional(),
  roomId: Yup.number().required('Requis'),
  generatorSlots: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().nullable().optional(),
        name: Yup.string()
          .max(100, '100 caractères maximum')
          .required('Requis'),
      }),
    )
    .test(
      'unique-slot-names',
      'Les noms des emplacements générateurs doivent être uniques',
      (slots) => {
        if (!slots) {
          return true
        }
        const names = slots.map(({ name }) => name).filter(Boolean)
        return names.length === new Set(names).size
      },
    ),
})

export const creditNoteChoiceSchema = Yup.object().shape({
  supplierContactIds: Yup.array()
    .of(Yup.string().uuid())
    .required('Requis')
    .min(1, 'Au moins 1 email'),
})

export const upsertWorkbenchGeneratorSchema = Yup.object().shape({
  generatorSlotId: Yup.string().required('Requis'),
  productBatchId: Yup.string().required('Requis'),
  storageLocationId: Yup.string().required('Requis'),
  serialNumber: Yup.string()
    .required('Requis')
    .max(120, 'Maximum 120 caractères'),
  activityAtRefGbq: Yup.number().typeError('Requis').min(0).required('Requis'),
  activityRefAt: Yup.date().typeError('Requis').required('Requis'),
})

export const prescriptionValidationSchema = Yup.object().shape({
  activityMBq: Yup.number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .moreThan(0, 'Doit être supérieur à 0')
    .test(
      'max-2-decimals',
      '2 décimales max',
      (value) => value === undefined || Number.isInteger(value * 100),
    )
    .required('Requis'),
  notes: Yup.string().nullable().max(255, '255 caractères maximum').optional(),
})

export const qcTestResultSchema = Yup.object({
  measuredAt: Yup.date().required('Requis').typeError('Date invalide'),
  observedNumericValue: Yup.number()
    .transform((val) => (isNaN(val) ? undefined : val))
    .when(['$isAnalytical', '$isCustomNumeric'], {
      is: (isAnalytical: boolean, isCustomNumeric: boolean) => isAnalytical || isCustomNumeric,
      then: (schema) =>
        schema
          .required('Requis')
          .test('ph-range', 'Le pH doit être compris entre 0 et 14', function (value) {
            const { qcType } = this.options.context as { qcType: QCType; isPercent: boolean }
            if (qcType !== QCType.PH) {
              return true
            }
            if (value == null) {
              return false
            }
            return value >= 0 && value <= 14
          })
          .test('percent-range', 'La valeur doit être comprise entre 0 et 100', function (value) {
            const { isPercent } = this.options.context as { qcType: QCType; isPercent: boolean }
            if (!isPercent) {
              return true
            }
            if (value == null) {
              return false
            }
            return value >= 0 && value <= 100
          })
          .test('max-2-decimals', '2 décimales max', (value) => value === undefined || Number.isInteger(value * 100)),
      otherwise: (schema) => schema.optional(),
    }),
  observedTextValue: Yup.string().when('observedDiffers', {
    is: true,
    then: (schema) => schema.trim().required('Requis'),
    otherwise: (schema) => schema.optional().default(''),
  }),
  observedBooleanValue: Yup.boolean()
    .when('$isCustomBoolean', {
      is: true,
      then: (schema) => schema.required('Requis'),
      otherwise: (schema) => schema.optional(),
    }),
  selectedOptionId: Yup.string().when('$isCustomList', {
    is: true,
    then: (schema) => schema.required('Requis'),
    otherwise: (schema) => schema.optional().default(''),
  }),
  observedDiffers: Yup.boolean().required().default(false),
  hotLabEquipmentId: Yup.string().optional(),
  notes: Yup.string().max(255, '255 caractères maximum').optional(),
})

export const invalidateQcResultSchema = Yup.object({
  comment: Yup.string().trim().required('Requis').max(500, '500 caractères maximum'),
  invalidatedByMemberId: Yup.number().required('Requis'),
})

export type PatientContactSchemaType = Yup.InferType<
  typeof patientContactSchema
>

export type PatientFormSchemaType = Yup.InferType<typeof patientFormSchema>
export type PatientSearchFormSchemaType = Yup.InferType<
  typeof patientSearchSchema
>
export type InsiSearchSchemaType = Yup.InferType<typeof insiSearchSchema>
export type MedicalOrderSchemaType = Yup.InferType<typeof MedicalOrderSchema>
export type VisitSchemaType = Yup.InferType<typeof visitSchema>

export const addOrganisationSchema = Yup.object().shape({
  name: Yup.string().required('validation.field.required'),
  address: Yup.string().required('validation.field.required'),
  zipCode: Yup.string().required('validation.field.required'),
  city: Yup.string().required('validation.field.required'),
  siteId: Yup.number().required('validation.field.required'),
  billingEmail: Yup.string()
    .email('validation.email.invalid')
    .nullable()
    .optional(),
  siret: Yup.string().nullable().optional(),

  chorusServiceCode: Yup.string().when('chorusEnabled', {
    is: 1,
    then: (schema) => schema.required('validation.field.required'),
    otherwise: (schema) => schema.nullable(),
  }),
  chorusEngagementNumber: Yup.string().when('chorusEnabled', {
    is: 1,
    then: (schema) => schema.required('validation.field.required'),
    otherwise: (schema) => schema.nullable(),
  }),
})

const needsActivity = (ctx: any) => {
  if (ctx?.forceActivity) {
    return true
  }
  const activityLevel = ctx?.container?.activityLevel
  return (activityLevel === ActivityLevel.LOW || activityLevel === ActivityLevel.HIGH)
    && (ctx?.container?.wasteEntryCount ?? 0) > 0
}

export const wasteEntrySchema = Yup.object().shape({
  containerId: Yup.string().required('Requis').typeError('Requis'),
  label: Yup.string().max(120, '120 caractères maximum').required('Requis'),
  description: Yup.string().nullable().optional(),
  isotopeId: Yup.string()
    .uuid()
    .nullable()
    .optional()
    .test('isotope-required', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) {
        return true
      }
      return value != null && value !== ''
    }),
  massG: Yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .min(0)
    .nullable()
    .optional(),
  volumeMl: Yup.number()
    .min(0)
    .nullable()
    .optional()
    .test('max-1-decimal', '1 décimale max', (value) => !value || Number.isInteger(value * 10)),
  activityAtRefMbq: Yup.number()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    )
    .min(0)
    .nullable()
    .optional()
    .test('max-2-decimals', '2 décimales max', (value) =>
      value == null || /^\d+(\.\d{1,2})?$/.test(value.toString())
    )
    .test('activity-required', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) {
        return true
      }
      return value != null
    }),
  activityRefAt: Yup.date()
    .nullable()
    .optional()
    .typeError('Date invalide')
    .test('activity-ref-required', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) {
        return true
      }
      return value != null
    }),
  collectedById: Yup.number().required('Requis').typeError('Requis'),
})

export const wasteEntryFromSourceSchema = wasteEntrySchema.pick([
  'containerId',
  'collectedById',
  'massG',
]).concat(Yup.object().shape({
  activityAtRefMbq: Yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .min(0)
    .required('Requis')
    .typeError('Requis')
    .test('max-2-decimals', '2 décimales max', (value) =>
      value == null || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  activityRefAt: Yup.date()
    .required('Requis')
    .typeError('Date invalide'),
  hotLabEquipmentId: Yup.string().uuid('UUID invalide').required('Requis'),
}))

export const wasteContainerMeasurementSchema = Yup.object().shape({
  hotLabEquipmentId: Yup.string()
    .uuid('UUID invalide')
    .test('required-if-activity', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) return true
      return !!value
    }),
  measurementCps: Yup.number()
    .transform((v, o) => (o === '' ? null : v))
    .min(0, 'Doit être positif')
    .nullable()
    .test('max-2-decimals', '2 décimales max', (v) => v == null || /^\d+(\.\d{1,2})?$/.test(v.toString()))
    .test('required-if-activity', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) return true
      return value != null
    }),
  backgroundNoiseCps: Yup.number()
    .transform((v, o) => (o === '' ? null : v))
    .min(0, 'Doit être positif')
    .nullable()
    .test('max-2-decimals', '2 décimales max', (v) => v == null || /^\d+(\.\d{1,2})?$/.test(v.toString()))
    .test('required-if-activity', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) return true
      return value != null
    }),
  distanceM: Yup.number()
    .transform((v, o) => (o === '' ? null : v))
    .min(0, 'Doit être positif')
    .nullable()
    .test('max-2-decimals', '2 décimales max', (v) => v == null || /^\d+(\.\d{1,2})?$/.test(v.toString()))
    .test('required-if-activity', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) return true
      return value != null
    }),
  measuredAt: Yup.date()
    .nullable()
    .test('required-if-activity', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) return true
      return value != null
    }),
  measuredById: Yup.number()
    .integer()
    .nullable()
    .test('required-if-activity', 'Requis', function (value) {
      if (!needsActivity(this.options.context)) return true
      return value != null
    }),
})

export const closeWasteContainerSchema = wasteContainerMeasurementSchema
export const disposeWasteContainerSchema = wasteContainerMeasurementSchema

export const transferWasteContainerSchema = Yup.object().shape({
  targetContainerId: Yup.string().uuid('UUID invalide').required('Requis'),
  transferredBy: Yup.number().integer().required('Requis'),
})

export const changeWasteEntryContainerSchema = Yup.object().shape({
  targetContainerId: Yup.string().uuid('UUID invalide').required('Conteneur de destination requis'),
})
